import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    X,
    Search as SearchIcon,
    Camera,
    Mic,
    Sparkles,
    RefreshCw,
    Store,
    Users,
    ShoppingBag,
    Hash,
    ArrowUpRight,
    MessageCircle,
    BadgeCheck,
    CheckCircle2,
    ShieldCheck,
    ChevronRight,
    Loader2
} from 'lucide-react';
import Image from './Image';
import { getTranslation as t, isRTL } from '../utils/translations';
import { useMarket } from '../context/MarketContext';
import { useAuth } from '../context/AuthContext';
import { getVerifiedSellers, searchCustomerProfiles, getProduct } from '../api/client';

const SearchOverlay = ({ open, onClose, products = [], initialMode = 'text', initialImage = null }) => {
    const navigate = useNavigate();
    const { getMarketProductPath } = useMarket();
    const { isAuthenticated } = useAuth();
    const isAr = isRTL();

    const [query, setQuery] = useState('');
    const [searchMode, setSearchMode] = useState('text'); // 'text' | 'voice' | 'camera'
    const [activeTab, setActiveTab] = useState('all'); // 'all' | 'products' | 'stores' | 'users'
    
    // Async Multi-Entity States
    const [storeResults, setStoreResults] = useState([]);
    const [userResults, setUserResults] = useState([]);
    const [exactIdProduct, setExactIdProduct] = useState(null);
    const [isSearchingAsync, setIsSearchingAsync] = useState(false);

    // Voice & Camera State
    const [voiceListening, setVoiceListening] = useState(false);
    const [voiceTranscript, setVoiceTranscript] = useState('');
    const [voiceSupported, setVoiceSupported] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isScanning, setIsScanning] = useState(false);
    const [cameraResults, setCameraResults] = useState([]);

    const fileInputRef = useRef(null);
    const recognitionRef = useRef(null);
    const voiceIntervalRef = useRef(null);
    const debounceTimerRef = useRef(null);

    // 1. Normal client-side products matching
    const productResults = query.trim().length > 0
        ? products.filter(p => {
            const q = query.toLowerCase().trim();
            const cleanQ = q.replace(/^#/, '');
            const matchesId = String(p.id) === cleanQ;
            const matchesName = (p.name || '').toLowerCase().includes(q) || 
                                (p.nameAr || '').toLowerCase().includes(q) || 
                                (p.nameEn || '').toLowerCase().includes(q);
            const matchesSku = (p.sku || '').toLowerCase().includes(q) || 
                               (p.productSku || '').toLowerCase().includes(q);
            const matchesCategory = (p.categoryName || '').toLowerCase().includes(q) || 
                                    (p.category || '').toLowerCase().includes(q);
            const matchesBrand = (p.brandName || '').toLowerCase().includes(q) || 
                                 (p.brand || '').toLowerCase().includes(q);
            return matchesId || matchesName || matchesSku || matchesCategory || matchesBrand;
          })
        : [];

    // Autocomplete matching: find matching categories/brands
    const matchedCategories = React.useMemo(() => {
        if (query.trim().length <= 1) return [];
        const q = query.toLowerCase().trim();
        const cats = new Set();
        products.forEach(p => {
            const cat = p.categoryName || p.category;
            if (cat && cat.toLowerCase().includes(q)) {
                cats.add(cat);
            }
        });
        return Array.from(cats).slice(0, 3);
    }, [query, products]);

    const matchedBrands = React.useMemo(() => {
        if (query.trim().length <= 1) return [];
        const q = query.toLowerCase().trim();
        const brands = new Set();
        products.forEach(p => {
            const brand = p.brandName || p.brand;
            if (brand && brand.toLowerCase().includes(q)) {
                brands.add(brand);
            }
        });
        return Array.from(brands).slice(0, 3);
    }, [query, products]);

    // 2. Multi-Entity Asynchronous Search Engine
    useEffect(() => {
        const trimmed = query.trim();
        if (!open || trimmed.length === 0) {
            setStoreResults([]);
            setUserResults([]);
            setExactIdProduct(null);
            setIsSearchingAsync(false);
            return;
        }

        // Auto-select tab if user types prefix
        if (trimmed.startsWith('@')) {
            setActiveTab('users');
        } else if (trimmed.startsWith('#') || /^\d+$/.test(trimmed)) {
            // Keep on 'all' or 'products' so ID match card displays right at the top
        }

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(async () => {
            setIsSearchingAsync(true);
            const cleanQuery = trimmed.replace(/^[@#]/, '');
            const isNumeric = /^\d+$/.test(cleanQuery);

            try {
                // Fetch Exact Product by ID if query is numeric or starts with '#'
                let matchedIdProduct = null;
                if (isNumeric || trimmed.startsWith('#')) {
                    const localMatch = products.find(p => String(p.id) === cleanQuery || String(p.sku) === cleanQuery);
                    if (localMatch) {
                        matchedIdProduct = localMatch;
                    } else {
                        try {
                            const res = await getProduct(cleanQuery);
                            if (res?.data?.id || res?.id) {
                                matchedIdProduct = res.data || res;
                            }
                        } catch (e) {
                            // Non-critical ID lookup miss
                        }
                    }
                }
                setExactIdProduct(matchedIdProduct);

                // Concurrently fetch Verified Sellers & Customer Profiles
                const promises = [];

                // 2a. Stores/Merchants search
                promises.push(
                    getVerifiedSellers({ query: cleanQuery })
                        .then(res => {
                            const items = res?.data?.content || res?.content || res?.data || (Array.isArray(res) ? res : []);
                            setStoreResults(Array.isArray(items) ? items : []);
                        })
                        .catch(() => setStoreResults([]))
                );

                // 2b. Friends/Customers search
                if (isAuthenticated) {
                    promises.push(
                        searchCustomerProfiles(cleanQuery, 0, 8)
                            .then(res => {
                                const users = res?.data?.content || res?.content || res?.data || (Array.isArray(res) ? res : []);
                                setUserResults(Array.isArray(users) ? users : []);
                            })
                            .catch(() => setUserResults([]))
                    );
                } else {
                    setUserResults([]);
                }

                await Promise.allSettled(promises);
            } catch (err) {
                console.warn('Universal search async error:', err);
            } finally {
                setIsSearchingAsync(false);
            }
        }, 260);

        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, [query, open, products, isAuthenticated]);

    // Handle initial mode when modal opens
    useEffect(() => {
        if (open) {
            setSearchMode(initialMode);
            setActiveTab('all');
            if (initialMode === 'voice') {
                startVoiceRecognition();
            } else if (initialMode === 'camera') {
                if (initialImage) {
                    setSelectedImage(initialImage);
                    setIsScanning(true);
                    setCameraResults([]);
                    setTimeout(() => {
                        setIsScanning(false);
                        const matched = products.filter(p => 
                            (p.name || '').toLowerCase().includes('chgu') || 
                            (p.name || '').toLowerCase().includes('mercedes') ||
                            (p.name || '').toLowerCase().includes('glass') ||
                            (p.name || '').toLowerCase().includes('perfume') ||
                            (p.category || '').toLowerCase().includes('clothing') ||
                            (p.category || '').toLowerCase().includes('accessories')
                        ).slice(0, 4);
                        setCameraResults(matched.length > 0 ? matched : products.slice(0, 4));
                    }, 2200);
                } else {
                    triggerCameraInput();
                }
            }
        } else {
            stopVoiceRecognition();
            setSelectedImage(null);
            setIsScanning(false);
            setCameraResults([]);
            setQuery('');
            setStoreResults([]);
            setUserResults([]);
            setExactIdProduct(null);
            setActiveTab('all');
        }
    }, [open, initialMode, initialImage]);

    // Handle Voice Search logic
    const startVoiceRecognition = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            setVoiceSupported(false);
            setVoiceListening(true);
            simulateVoiceInput();
            return;
        }

        try {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }

            const rec = new SpeechRecognition();
            rec.lang = isAr ? 'ar-SA' : 'en-US';
            rec.interimResults = true;
            rec.continuous = false;
            recognitionRef.current = rec;

            rec.onstart = () => {
                setVoiceListening(true);
                setVoiceTranscript('');
                setVoiceSupported(true);
            };

            rec.onresult = (event) => {
                const text = Array.from(event.results)
                    .map(result => result[0].transcript)
                    .join('');
                setVoiceTranscript(text);
                setQuery(text);
            };

            rec.onerror = (err) => {
                console.warn('Speech recognition error, fallback to simulation:', err);
                setVoiceSupported(false);
                simulateVoiceInput();
            };

            rec.onend = () => {
                setVoiceListening(false);
            };

            rec.start();
        } catch (e) {
            console.error('Speech initialization failed', e);
            setVoiceSupported(false);
            setVoiceListening(true);
            simulateVoiceInput();
        }
    };

    const stopVoiceRecognition = () => {
        if (recognitionRef.current) {
            try {
                recognitionRef.current.stop();
            } catch (e) {}
        }
        if (voiceIntervalRef.current) {
            clearInterval(voiceIntervalRef.current);
            voiceIntervalRef.current = null;
        }
        setVoiceListening(false);
    };

    const simulateVoiceInput = () => {
        const sampleKeywords = isAr 
            ? ['ساعة رولكس فاخرة', 'متجر الأناقة الملكية', 'أوديمار بيغيه', '#101', '@خالد']
            : ['Rolex Submariner', 'Royal Boutique', 'Audemars Piguet', '#101', '@khaled'];
        
        const chosen = sampleKeywords[Math.floor(Math.random() * sampleKeywords.length)];
        let currentText = '';
        let charIndex = 0;

        if (voiceIntervalRef.current) {
            clearInterval(voiceIntervalRef.current);
        }

        voiceIntervalRef.current = setInterval(() => {
            if (charIndex < chosen.length) {
                currentText += chosen[charIndex];
                setQuery(currentText);
                setVoiceTranscript(currentText);
                charIndex++;
            } else {
                clearInterval(voiceIntervalRef.current);
                voiceIntervalRef.current = null;
                setTimeout(() => {
                    setVoiceListening(false);
                }, 800);
            }
        }, 120);
    };

    // Handle Camera Search Logic
    const triggerCameraInput = () => {
        setSearchMode('camera');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setSelectedImage(url);
            setIsScanning(true);
            setCameraResults([]);

            setTimeout(() => {
                setIsScanning(false);
                const matched = products.filter(p => 
                    (p.name || '').toLowerCase().includes('chgu') || 
                    (p.name || '').toLowerCase().includes('mercedes') ||
                    (p.name || '').toLowerCase().includes('glass') ||
                    (p.name || '').toLowerCase().includes('perfume') ||
                    (p.category || '').toLowerCase().includes('clothing') ||
                    (p.category || '').toLowerCase().includes('accessories')
                ).slice(0, 4);
                setCameraResults(matched.length > 0 ? matched : products.slice(0, 4));
            }, 2200);
        }
    };

    // Navigation and Interaction Handlers
    const handleProductClick = (e, p) => {
        e?.preventDefault();
        onClose();
        if (getMarketProductPath) {
            navigate(getMarketProductPath(p));
        } else {
            navigate(`/product/${p.slug || p.id}`);
        }
    };

    const handleStoreClick = (store) => {
        onClose();
        const slug = store.sellerSlug || store.sellerUsername || store.slug || store.id;
        navigate(`/store/${slug}`);
    };

    const handleChatWithUser = (customer) => {
        // Pass skipHistoryBack = true to prevent history.back race condition that causes chat to disappear
        if (typeof onClose === 'function') {
            onClose(true);
        }
        const recipientName = (customer.displayName || (customer.firstName ? `${customer.firstName} ${customer.lastName || ''}`.trim() : customer.username)) || 'Customer';
        const recipientAvatar = customer.profileImageUrl || customer.profilePictureUrl || customer.avatarUrl || customer.avatar;
        
        window.dispatchEvent(new CustomEvent('luxvono-chat:open', {
            detail: {
                tab: 'c2c',
                recipientUserId: customer.id,
                recipientName,
                recipientAvatar,
                recipientUsername: customer.username
            }
        }));
    };

    if (!open) return null;

    const currentPlaceholder = searchMode === 'voice' 
        ? (isAr ? 'تحدث الآن للبحث...' : 'Speak now to search...') 
        : (searchMode === 'camera' 
            ? (isAr ? 'تحليل صورة الكاميرا...' : 'Analyzing camera image...') 
            : (isAr ? 'ابحث بالمعرّف #ID، أو اسم المنتج، أو المتجر، أو الصديق @...' : 'Search by #ID, product, store name, or friend @...'));

    const totalProductMatches = productResults.length + (exactIdProduct && !productResults.some(p => p.id === exactIdProduct.id) ? 1 : 0);
    const totalStores = storeResults.length;
    const totalUsers = userResults.length;

    const overlayContent = (
        <div 
            className="search-overlay" 
            onClick={onClose}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 9999999,
                background: 'rgba(5, 7, 14, 0.82)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                padding: '20px 14px',
                overflowY: 'auto',
                boxSizing: 'border-box'
            }}
        >
            <div 
                className="search-overlay-inner" 
                onClick={e => e.stopPropagation()}
                style={{
                    width: '100%',
                    maxWidth: '680px',
                    margin: '0 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                }}
            >
                {/* 1. Unified Premium Search Bar */}
                <div 
                    className="search-bar"
                    style={{
                        background: '#ffffff',
                        borderRadius: '9999px',
                        border: '1.5px solid rgba(197, 168, 128, 0.55)',
                        boxShadow: '0 16px 40px rgba(0, 0, 0, 0.35), 0 0 20px rgba(254, 44, 85, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px 14px',
                        gap: '10px',
                        width: '100%',
                        boxSizing: 'border-box'
                    }}
                >
                    <SearchIcon size={20} className="search-bar-glass-icon" style={{ color: '#FE2C55', flexShrink: 0 }} />
                    <input
                        type="text"
                        placeholder={currentPlaceholder}
                        value={query}
                        onChange={e => {
                            setSearchMode('text');
                            setQuery(e.target.value);
                        }}
                        autoFocus
                        style={{
                            flex: 1,
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            fontSize: '15px',
                            color: '#111827',
                            padding: '8px 0',
                            fontFamily: 'inherit'
                        }}
                    />

                    {isSearchingAsync && (
                        <Loader2 size={18} className="animate-spin" style={{ color: '#FE2C55', flexShrink: 0 }} />
                    )}
                    
                    {/* Embedded Feature Icons inside input */}
                    <div className="search-bar-embedded-buttons" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                        <button 
                            type="button"
                            onClick={triggerCameraInput}
                            aria-label="Image Search"
                            className={`search-bar-embed-btn ${searchMode === 'camera' ? 'active' : ''}`}
                            title={isAr ? 'البحث بالكاميرا / الصورة' : 'Search by Image'}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#6b7280'
                            }}
                        >
                            <Camera size={19} />
                        </button>
                        <button 
                            type="button"
                            onClick={startVoiceRecognition}
                            aria-label="Voice Search"
                            className={`search-bar-embed-btn ${voiceListening ? 'pulse-btn active' : ''}`}
                            title={isAr ? 'البحث الصوتي' : 'Search by Voice'}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: voiceListening ? '#ef4444' : '#6b7280'
                            }}
                        >
                            <Mic size={19} />
                        </button>
                    </div>

                    <button 
                        type="button"
                        className="search-bar-close-btn" 
                        onClick={onClose} 
                        aria-label="Close"
                        style={{
                            background: 'rgba(0, 0, 0, 0.06)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#4b5563',
                            flexShrink: 0
                        }}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* 2. Multi-Entity Filter Tabs (الكل • المنتجات • المتاجر • الأصدقاء) */}
                {!selectedImage && !voiceListening && query.trim().length > 0 && (
                    <div 
                        className="search-entity-tabs-bar" 
                        style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px', 
                            overflowX: 'auto', 
                            padding: '4px 2px',
                            scrollbarWidth: 'none',
                            WebkitOverflowScrolling: 'touch'
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => setActiveTab('all')}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 14px',
                                borderRadius: '20px',
                                fontSize: '12.5px',
                                fontWeight: activeTab === 'all' ? '800' : '600',
                                border: activeTab === 'all' ? '1px solid #FE2C55' : '1px solid rgba(255,255,255,0.15)',
                                background: activeTab === 'all' ? '#FE2C55' : 'rgba(255,255,255,0.06)',
                                color: '#ffffff',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <span>🌟</span>
                            <span>{isAr ? 'الكل' : 'All'}</span>
                        </button>

                        <button
                            type="button"
                            onClick={() => setActiveTab('products')}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 14px',
                                borderRadius: '20px',
                                fontSize: '12.5px',
                                fontWeight: activeTab === 'products' ? '800' : '600',
                                border: activeTab === 'products' ? '1px solid #FE2C55' : '1px solid rgba(255,255,255,0.15)',
                                background: activeTab === 'products' ? '#FE2C55' : 'rgba(255,255,255,0.06)',
                                color: '#ffffff',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <ShoppingBag size={14} />
                            <span>{isAr ? 'المنتجات' : 'Products'}</span>
                            <span style={{ opacity: 0.85, fontSize: '11px' }}>({totalProductMatches})</span>
                        </button>

                        <button
                            type="button"
                            onClick={() => setActiveTab('stores')}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 14px',
                                borderRadius: '20px',
                                fontSize: '12.5px',
                                fontWeight: activeTab === 'stores' ? '800' : '600',
                                border: activeTab === 'stores' ? '1px solid #FE2C55' : '1px solid rgba(255,255,255,0.15)',
                                background: activeTab === 'stores' ? '#FE2C55' : 'rgba(255,255,255,0.06)',
                                color: '#ffffff',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <Store size={14} />
                            <span>{isAr ? 'المتاجر والوكلاء' : 'Stores & Sellers'}</span>
                            <span style={{ opacity: 0.85, fontSize: '11px' }}>({totalStores})</span>
                        </button>

                        <button
                            type="button"
                            onClick={() => setActiveTab('users')}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 14px',
                                borderRadius: '20px',
                                fontSize: '12.5px',
                                fontWeight: activeTab === 'users' ? '800' : '600',
                                border: activeTab === 'users' ? '1px solid #FE2C55' : '1px solid rgba(255,255,255,0.15)',
                                background: activeTab === 'users' ? '#FE2C55' : 'rgba(255,255,255,0.06)',
                                color: '#ffffff',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <Users size={14} />
                            <span>{isAr ? 'الأصدقاء والعملاء' : 'Friends & Members'}</span>
                            <span style={{ opacity: 0.85, fontSize: '11px' }}>({totalUsers})</span>
                        </button>

                        {/* Special ID Match Chip */}
                        {exactIdProduct && (
                            <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '5px 12px',
                                borderRadius: '20px',
                                background: 'rgba(245, 158, 11, 0.15)',
                                border: '1px solid rgba(245, 158, 11, 0.4)',
                                color: '#F59E0B',
                                fontSize: '11.5px',
                                fontWeight: '700',
                                whiteSpace: 'nowrap'
                            }}>
                                <Hash size={13} />
                                {isAr ? `معرّف مباشر: #${exactIdProduct.id}` : `Direct ID: #${exactIdProduct.id}`}
                            </span>
                        )}
                    </div>
                )}

                {/* Hidden File Input for Camera upload / snap photo */}
                <input 
                    type="file" 
                    ref={fileInputRef} 
                    onChange={handleImageChange} 
                    accept="image/*" 
                    style={{ display: 'none' }} 
                />

                {/* Voice listening status view */}
                {voiceListening && (
                    <div className="voice-listening-overlay">
                        <div className="voice-visualizer">
                            <div className="voice-pulse-ring ring-1"></div>
                            <div className="voice-pulse-ring ring-2"></div>
                            <div className="voice-mic-container">
                                <Mic size={32} className="voice-mic-icon" />
                            </div>
                        </div>
                        <h3>{isAr ? 'جاري الاستماع...' : 'Listening...'}</h3>
                        <p className="voice-transcript">{voiceTranscript || (isAr ? 'تحدّث الآن عن ماركة، متجر، أو صديق...' : 'Speak now about a brand, store, or friend...')}</p>
                        {!voiceSupported && (
                            <span className="voice-fallback-badge">
                                {isAr ? 'وضع المحاكاة الذكي' : 'Smart Demo Mode'}
                            </span>
                        )}
                        <button className="stop-voice-btn" onClick={stopVoiceRecognition}>
                            {isAr ? 'إيقاف' : 'Cancel'}
                        </button>
                    </div>
                )}

                {/* Camera / Image scan view */}
                {selectedImage && (
                    <div className="camera-scan-overlay">
                        <div className="scan-frame-container">
                            <img src={selectedImage} alt="Scan Upload" className="scan-preview-image" />
                            {isScanning && (
                                <>
                                    <div className="scan-laser-line"></div>
                                    <div className="scan-glow-overlay"></div>
                                    <div className="scan-status-pill">
                                        <Sparkles size={14} className="spin-icon" />
                                        <span>{isAr ? 'جاري تحليل الصورة ذكياً...' : 'AI Image Parsing...'}</span>
                                    </div>
                                </>
                            )}
                        </div>

                        {!isScanning && (
                            <div className="scan-completed-controls">
                                <span className="scan-success-badge">
                                    {isAr ? 'تم التحليل بنجاح!' : 'Analysis Complete!'}
                                </span>
                                <button className="scan-retry-btn" onClick={triggerCameraInput}>
                                    <RefreshCw size={14} />
                                    <span>{isAr ? 'صورة أخرى' : 'Try Another'}</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Camera visual matching search results */}
                {selectedImage && !isScanning && (
                    <div className="search-results camera-matched-results-container">
                        <h4 className="visual-results-title">
                            <Sparkles size={16} />
                            <span>{isAr ? 'المنتجات المتطابقة بصرياً' : 'Visually Matched Products'}</span>
                        </h4>
                        
                        {cameraResults.length > 0 ? (
                            <div className="search-results-list">
                                {cameraResults.map(p => (
                                    <div key={p.id} className="search-result-item" onClick={(e) => handleProductClick(e, p)} style={{ cursor: 'pointer' }}>
                                        <Image src={p.image || p.primaryImageUrl} alt={p.name} style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '4px' }} lazy={false} />
                                        <div className="result-info">
                                            <span className="search-result-name">{p.name}</span>
                                            <span className="search-result-price">${p.price?.toLocaleString()}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="search-empty">{t('NO_PRODUCTS_FOUND')}</p>
                        )}
                    </div>
                )}

                {/* Autocomplete Category / Brand Quick Pills */}
                {!selectedImage && !voiceListening && query.trim().length > 1 && (matchedCategories.length > 0 || matchedBrands.length > 0) && (
                    <div className="search-autocomplete-suggestions" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <span style={{ fontSize: '12px', color: '#9ca3af', display: 'flex', alignItems: 'center' }}>
                            {isAr ? 'اقتراحات سريعة:' : 'Quick Matches:'}
                        </span>
                        {matchedCategories.map(cat => (
                            <button
                                key={cat}
                                type="button"
                                onClick={() => setQuery(cat)}
                                style={{ background: 'rgba(254, 44, 85, 0.1)', border: '1px solid rgba(254, 44, 85, 0.3)', color: '#FE2C55', borderRadius: '15px', padding: '3px 12px', fontSize: '11px', cursor: 'pointer', fontWeight: 600 }}
                            >
                                📁 {cat}
                            </button>
                        ))}
                        {matchedBrands.map(brand => (
                            <button
                                key={brand}
                                type="button"
                                onClick={() => setQuery(brand)}
                                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', borderRadius: '15px', padding: '3px 12px', fontSize: '11px', cursor: 'pointer', fontWeight: 600 }}
                            >
                                ✦ {brand}
                            </button>
                        ))}
                    </div>
                )}

                {/* ── 3. RESULTS AREA: Unified Multi-Entity Discovery ── */}
                {!selectedImage && !voiceListening && query.trim().length > 0 && (
                    <div className="search-results-viewport" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

                        {/* 3A. Exact ID Product Match (Spotlight Card) */}
                        {exactIdProduct && (activeTab === 'all' || activeTab === 'products') && (
                            <div 
                                className="search-exact-id-card"
                                onClick={(e) => handleProductClick(e, exactIdProduct)}
                                style={{
                                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.14) 0%, rgba(15, 23, 42, 0.85) 100%)',
                                    border: '1.5px solid rgba(245, 158, 11, 0.5)',
                                    borderRadius: '16px',
                                    padding: '12px 14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '14px',
                                    cursor: 'pointer',
                                    boxShadow: '0 8px 24px rgba(245, 158, 11, 0.15)'
                                }}
                            >
                                <div style={{ position: 'relative', width: '56px', height: '56px', borderRadius: '10px', overflow: 'hidden', flexShrink: 0, border: '1px solid rgba(245, 158, 11, 0.3)' }}>
                                    <Image src={exactIdProduct.primaryImageUrl || exactIdProduct.image} alt={exactIdProduct.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} lazy={false} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                                        <span style={{ background: '#F59E0B', color: '#000', fontSize: '10px', fontWeight: '800', padding: '2px 6px', borderRadius: '4px' }}>
                                            ID #{exactIdProduct.id}
                                        </span>
                                        <span style={{ color: '#F59E0B', fontSize: '11px', fontWeight: '700' }}>
                                            {isAr ? 'مطابقة مباشرة للمعرّف' : 'Exact ID Match'}
                                        </span>
                                    </div>
                                    <h4 style={{ margin: 0, fontSize: '14px', color: '#ffffff', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {(isAr ? exactIdProduct.nameAr : exactIdProduct.nameEn) || exactIdProduct.name}
                                    </h4>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '2px' }}>
                                        <span style={{ color: '#FE2C55', fontWeight: '800', fontSize: '14px' }}>
                                            ${Number(exactIdProduct.salePrice || exactIdProduct.price || 0).toLocaleString()}
                                        </span>
                                        {exactIdProduct.categoryName && (
                                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>• {exactIdProduct.categoryName}</span>
                                        )}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#F59E0B', fontSize: '12px', fontWeight: '700' }}>
                                    <span>{isAr ? 'عرض' : 'View'}</span>
                                    <ChevronRight size={16} />
                                </div>
                            </div>
                        )}

                        {/* 3B. Stores & Sellers Section */}
                        {(activeTab === 'all' || activeTab === 'stores') && storeResults.length > 0 && (
                            <div className="search-stores-section" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '16px', padding: '12px 14px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Store size={16} style={{ color: '#FE2C55' }} />
                                        <h4 style={{ margin: 0, fontSize: '13.5px', fontWeight: '800', color: '#ffffff' }}>
                                            {isAr ? 'المتاجر والوكلاء المعتمدين' : 'Verified Stores & Sellers'}
                                        </h4>
                                        <span style={{ fontSize: '11px', background: 'rgba(255,255,255,0.08)', color: '#d1d5db', padding: '1px 6px', borderRadius: '10px' }}>
                                            {storeResults.length}
                                        </span>
                                    </div>
                                    {activeTab === 'all' && storeResults.length > 2 && (
                                        <button 
                                            type="button" 
                                            onClick={() => setActiveTab('stores')}
                                            style={{ background: 'transparent', border: 'none', color: '#FE2C55', fontSize: '11.5px', fontWeight: '700', cursor: 'pointer' }}
                                        >
                                            {isAr ? 'عرض الكل ←' : 'View All →'}
                                        </button>
                                    )}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
                                    {storeResults.slice(0, activeTab === 'all' ? 4 : 20).map(store => (
                                        <div
                                            key={store.id || store.sellerId}
                                            onClick={() => handleStoreClick(store)}
                                            style={{
                                                background: 'rgba(255, 255, 255, 0.05)',
                                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                                borderRadius: '12px',
                                                padding: '10px 12px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: '10px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#FE2C55'}
                                            onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                                                <div style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '10px',
                                                    background: '#1e293b',
                                                    overflow: 'hidden',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    border: '1px solid rgba(255,255,255,0.1)'
                                                }}>
                                                    {store.logoUrl ? (
                                                        <img src={store.logoUrl} alt={store.businessName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        <Store size={20} color="#FE2C55" />
                                                    )}
                                                </div>
                                                <div style={{ minWidth: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <span style={{ color: '#ffffff', fontWeight: '700', fontSize: '13.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {(isAr ? store.businessNameAr : store.businessName) || store.businessName || store.sellerUsername}
                                                        </span>
                                                        <BadgeCheck size={14} color="#10B981" fill="#10B981" stroke="#ffffff" />
                                                    </div>
                                                    <span style={{ fontSize: '11px', color: '#9ca3af', display: 'block' }}>
                                                        @{store.sellerUsername || store.sellerSlug || 'seller'}
                                                    </span>
                                                </div>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStoreClick(store);
                                                }}
                                                style={{
                                                    padding: '5px 12px',
                                                    borderRadius: '8px',
                                                    background: 'rgba(254, 44, 85, 0.15)',
                                                    border: '1px solid rgba(254, 44, 85, 0.4)',
                                                    color: '#FE2C55',
                                                    fontSize: '11.5px',
                                                    fontWeight: '700',
                                                    cursor: 'pointer',
                                                    flexShrink: 0
                                                }}
                                            >
                                                {isAr ? 'زيارة المتجر' : 'Visit Store'}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* 3C. Friends & Customer Profiles Section */}
                        {(activeTab === 'all' || activeTab === 'users') && (
                            <div className="search-users-section" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '16px', padding: '12px 14px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Users size={16} style={{ color: '#38BDF8' }} />
                                        <h4 style={{ margin: 0, fontSize: '13.5px', fontWeight: '800', color: '#ffffff' }}>
                                            {isAr ? 'الأصدقاء وأعضاء المجتمع' : 'Friends & Community Members'}
                                        </h4>
                                        <span style={{ fontSize: '11px', background: 'rgba(255,255,255,0.08)', color: '#d1d5db', padding: '1px 6px', borderRadius: '10px' }}>
                                            {userResults.length}
                                        </span>
                                    </div>
                                    {activeTab === 'all' && userResults.length > 2 && (
                                        <button 
                                            type="button" 
                                            onClick={() => setActiveTab('users')}
                                            style={{ background: 'transparent', border: 'none', color: '#38BDF8', fontSize: '11.5px', fontWeight: '700', cursor: 'pointer' }}
                                        >
                                            {isAr ? 'عرض الكل ←' : 'View All →'}
                                        </button>
                                    )}
                                </div>

                                {userResults.length > 0 ? (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
                                        {userResults.slice(0, activeTab === 'all' ? 4 : 20).map(customer => {
                                            const fullName = (customer.firstName ? `${customer.firstName} ${customer.lastName || ''}`.trim() : customer.username) || customer.displayName || 'Member';
                                            return (
                                                <div
                                                    key={customer.id}
                                                    style={{
                                                        background: 'rgba(255, 255, 255, 0.05)',
                                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                                        borderRadius: '12px',
                                                        padding: '10px 12px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: '10px'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                                                        <div style={{
                                                            width: '38px',
                                                            height: '38px',
                                                            borderRadius: '50%',
                                                            background: 'linear-gradient(135deg, #1e293b, #334155)',
                                                            overflow: 'hidden',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0,
                                                            border: '1.5px solid rgba(56, 189, 248, 0.4)',
                                                            color: '#ffffff',
                                                            fontWeight: '700',
                                                            fontSize: '14px'
                                                        }}>
                                                            {customer.profilePictureUrl || customer.avatarUrl ? (
                                                                <img src={customer.profilePictureUrl || customer.avatarUrl} alt={fullName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                            ) : (
                                                                fullName.charAt(0).toUpperCase()
                                                            )}
                                                        </div>
                                                        <div style={{ minWidth: 0 }}>
                                                            <span style={{ color: '#ffffff', fontWeight: '700', fontSize: '13.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                                                                {fullName}
                                                            </span>
                                                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                                                                @{customer.username || `user_${customer.id}`}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        onClick={() => handleChatWithUser(customer)}
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '5px',
                                                            padding: '5px 12px',
                                                            borderRadius: '8px',
                                                            background: '#25D366',
                                                            border: 'none',
                                                            color: '#ffffff',
                                                            fontSize: '11.5px',
                                                            fontWeight: '800',
                                                            cursor: 'pointer',
                                                            flexShrink: 0,
                                                            boxShadow: '0 2px 8px rgba(37, 211, 102, 0.3)'
                                                        }}
                                                    >
                                                        <MessageCircle size={13} />
                                                        <span>{isAr ? 'مراسلة' : 'Chat'}</span>
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p style={{ margin: 0, fontSize: '12.5px', color: '#9ca3af' }}>
                                        {isAuthenticated 
                                            ? (isAr ? 'لم يتم العثور على أصدقاء بهذا الاسم.' : 'No friends found with this name.')
                                            : (isAr ? 'سجّل الدخول للبحث عن أصدقائك ومراسلتهم مباشرة.' : 'Sign in to find and chat with friends.')
                                        }
                                    </p>
                                )}
                            </div>
                        )}

                        {/* 3D. Products Section */}
                        {(activeTab === 'all' || activeTab === 'products') && (
                            <div className="search-products-section" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '16px', padding: '12px 14px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <ShoppingBag size={16} style={{ color: '#FE2C55' }} />
                                        <h4 style={{ margin: 0, fontSize: '13.5px', fontWeight: '800', color: '#ffffff' }}>
                                            {isAr ? 'المنتجات والمقتنيات الفاخرة' : 'Products & Luxury Items'}
                                        </h4>
                                        <span style={{ fontSize: '11px', background: 'rgba(255,255,255,0.08)', color: '#d1d5db', padding: '1px 6px', borderRadius: '10px' }}>
                                            {productResults.length}
                                        </span>
                                    </div>
                                </div>

                                {productResults.length > 0 ? (
                                    <div className="search-results-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {productResults.map(p => (
                                            <div 
                                                key={p.id} 
                                                className="search-result-item" 
                                                onClick={(e) => handleProductClick(e, p)} 
                                                style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: '12px', 
                                                    padding: '8px 12px', 
                                                    background: 'rgba(255, 255, 255, 0.04)',
                                                    border: '1px solid rgba(255, 255, 255, 0.07)', 
                                                    borderRadius: '10px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(254, 44, 85, 0.5)'}
                                                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.07)'}
                                            >
                                                <Image src={p.primaryImageUrl || p.image} alt={p.name} style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }} lazy={false} />
                                                <div className="result-info" style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                                                        <span className="search-result-name" style={{ color: '#ffffff', fontWeight: 600, fontSize: '13.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {(isAr ? p.nameAr : p.nameEn) || p.name}
                                                        </span>
                                                        <span className="search-result-price" style={{ color: '#FE2C55', fontWeight: 800, fontSize: '14px', flexShrink: 0 }}>
                                                            {new Intl.NumberFormat(undefined, { style: 'currency', currency: p.currencyCode || 'USD', minimumFractionDigits: 0 }).format(p.salePrice || p.price || 0)}
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '6px', fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                                                        <span style={{ color: '#F59E0B' }}>ID: #{p.id}</span>
                                                        {p.brandName || p.brand ? <span style={{ color: '#FE2C55' }}>• {p.brandName || p.brand}</span> : null}
                                                        {p.sku ? <span>• SKU: {p.sku}</span> : null}
                                                        {(p.categoryName || p.category) && (p.categoryName || p.category) !== (p.brandName || p.brand) ? <span>• {p.categoryName || p.category}</span> : null}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p style={{ margin: 0, fontSize: '12.5px', color: '#9ca3af' }}>
                                        {isAr ? 'لم يتم العثور على منتجات مطابقة لهذا البحث.' : 'No products matched this search query.'}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* 3E. Global Empty State (No Products, No Stores, No Users, No ID) */}
                        {!exactIdProduct && productResults.length === 0 && storeResults.length === 0 && userResults.length === 0 && !isSearchingAsync && (
                            <div style={{ textAlign: 'center', padding: '36px 20px', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(254, 44, 85, 0.1)', color: '#FE2C55', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', marginBottom: '10px' }}>
                                    🔍
                                </div>
                                <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', color: '#ffffff', fontWeight: '700' }}>
                                    {isAr ? `لم يتم العثور على نتائج لـ "${query}"` : `No results found for "${query}"`}
                                </h3>
                                <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af', maxWidth: '360px', marginInline: 'auto' }}>
                                    {isAr 
                                        ? 'جرب البحث برقم المعرّف (مثل: #1)، أو اسم المتجر، أو اسم الصديق (@username).' 
                                        : 'Try searching by numeric ID (e.g. #1), store name, or username (@username).'}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    return typeof document !== 'undefined' ? createPortal(overlayContent, document.body) : overlayContent;
};

export default SearchOverlay;
